import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Phone,
  EnvelopeSimple,
  MapPin,
  PaperPlaneTilt,
  CheckCircle,
  Clock,
} from "@phosphor-icons/react";
import { submitContact } from "../lib/api";
import { COMPANY } from "../lib/constants";
import { toast } from "sonner";

const HEATING = [
  { value: "fioul", label: "Fioul" },
  { value: "gaz", label: "Gaz" },
  { value: "electrique", label: "Électrique" },
  { value: "autre", label: "Autre" },
];

export default function Contact() {
  const [data, setData] = useState({
    name: "",
    phone: "",
    postal_code: "",
    heating_type: "gaz",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!data.name.trim() || !data.phone.trim() || !data.postal_code.trim()) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(data);
      setDone(true);
      toast.success("Demande envoyée. Nous vous recontactons rapidement.");
    } catch (err) {
      toast.error("Erreur lors de l'envoi. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <section className="eco-gradient py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-700 mb-3">
            Contact
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight max-w-3xl">
            Parlons de votre projet
          </h1>
          <p className="text-slate-600 mt-4 max-w-2xl">
            Remplissez le formulaire ci-dessous ou appelez-nous directement. Un
            conseiller Hélios Solutions vous recontactera rapidement.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid="contact-success"
                className="bg-white p-10 rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.05)] text-center"
              >
                <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                  <CheckCircle size={30} weight="fill" color="#10B981" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Merci, votre demande a bien été envoyée
                </h2>
                <p className="text-slate-600 max-w-md mx-auto">
                  Un conseiller vous recontactera rapidement pour échanger sur
                  votre projet.
                </p>
              </motion.div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="bg-white p-6 md:p-10 rounded-2xl border border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.05)]"
              >
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  Formulaire de contact
                </h2>
                <p className="text-sm text-slate-500 mb-6">
                  Étude personnalisée et sans engagement. Aides disponibles
                  selon votre situation.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field
                    label="Nom *"
                    testId="contact-name"
                    value={data.name}
                    onChange={(v) => setData({ ...data, name: v })}
                    placeholder="Votre nom"
                  />
                  <Field
                    label="Téléphone *"
                    testId="contact-phone"
                    type="tel"
                    value={data.phone}
                    onChange={(v) => setData({ ...data, phone: v })}
                    placeholder="06 XX XX XX XX"
                  />
                  <Field
                    label="Code postal *"
                    testId="contact-cp"
                    value={data.postal_code}
                    onChange={(v) =>
                      setData({ ...data, postal_code: v.replace(/\D/g, "").slice(0, 5) })
                    }
                    placeholder="Ex : 75001"
                  />
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      Type de chauffage actuel
                    </label>
                    <select
                      data-testid="contact-heating"
                      value={data.heating_type}
                      onChange={(e) =>
                        setData({ ...data, heating_type: e.target.value })
                      }
                      className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none bg-white"
                    >
                      {HEATING.map((h) => (
                        <option key={h.value} value={h.value}>
                          {h.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={loading}
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl font-semibold btn-primary-eco disabled:opacity-60"
                >
                  {loading ? "Envoi..." : "Être rappelé rapidement"}
                  {!loading && <PaperPlaneTilt size={16} weight="fill" />}
                </button>
                <p className="text-xs text-slate-700 text-center mt-3 font-medium">
                  Nous vous rappelons rapidement après votre demande
                </p>
                <p className="text-xs text-slate-500 text-center mt-1">
                  Sans engagement. Reste à charge variable selon profil.
                </p>
              </form>
            )}
          </div>

          <aside className="lg:col-span-2 space-y-5">
            <div className="p-6 rounded-2xl bg-gradient-to-br from-blue-700 to-blue-900 text-white">
              <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-widest text-blue-100">
                <Phone size={14} weight="fill" /> Appel direct
              </div>
              <a
                href={COMPANY.phoneHref}
                data-testid="contact-phone-link"
                className="text-3xl font-heading font-bold hover:text-emerald-200 transition-colors"
              >
                {COMPANY.phone}
              </a>
              <p className="text-blue-100 text-sm mt-2">
                Un conseiller Hélios Solutions vous répond directement.
              </p>
            </div>

            <div className="p-6 rounded-2xl bg-white border border-slate-200">
              <h3 className="font-heading font-semibold text-slate-900 mb-3">
                Informations
              </h3>
              <ul className="space-y-3 text-sm text-slate-700">
                <li className="flex items-start gap-2">
                  <EnvelopeSimple size={18} className="text-blue-700 flex-shrink-0 mt-0.5" />
                  <span className="break-all">{COMPANY.email}</span>
                </li>
                <li className="flex items-start gap-2">
                  <MapPin size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                  <span>{COMPANY.zone}</span>
                </li>
                <li className="flex items-start gap-2">
                  <Clock size={18} className="text-blue-700 flex-shrink-0 mt-0.5" />
                  <span>Lun. – Sam. • 9h – 19h</span>
                </li>
              </ul>
            </div>

            <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-100">
              <h3 className="font-heading font-semibold text-slate-900 mb-1">
                Étude personnalisée
              </h3>
              <p className="text-sm text-slate-700">
                Chaque projet est étudié de manière individuelle. Aides
                potentiellement mobilisables selon votre situation.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function Field({ label, testId, value, onChange, type = "text", placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        {label}
      </label>
      <input
        type={type}
        data-testid={`${testId}-input`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none"
        required
      />
    </div>
  );
}
